import React, { useMemo, useState } from "react";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import WorkOutlineIcon from "@mui/icons-material/WorkOutline";
import CircularProgress from "@mui/material/CircularProgress";
import { postRightworkAPI } from "../requests/utilities/requests";
import { API_ops_assistant_confirm_action } from "../../../constants";
import { toast } from "react-toastify";

/**
 * Setup Assistant bulk roles card (create_roles): the suggested roles grouped
 * by department, each with a checkbox, plus an "add a role" field per
 * department. Confirm sends the final list; the executor re-validates it.
 */
export default function RolesProposalCard({
  proposal,
  sessionUuid,
  homeStore,
  onStatusChange,
  confirmUrl = API_ops_assistant_confirm_action,
  scopeParams,
}) {
  const summary = proposal.summary || {};
  const departments = useMemo(
    () => (Array.isArray(summary.departments) && summary.departments.length > 0
      ? summary.departments
      : [...new Set((summary.rows || []).map((r) => r.department_name))]),
    [summary.departments, summary.rows]
  );
  const [rows, setRows] = useState(() =>
    (Array.isArray(summary.rows) ? summary.rows : []).map((r, i) => ({
      key: `${r.department_name}:${String(r.role_name).toLowerCase()}:${i}`,
      role_name: r.role_name,
      department_name: r.department_name,
      exists: Boolean(r.exists),
      checked: !r.exists,
    }))
  );
  const [drafts, setDrafts] = useState({});
  const [status, setStatus] = useState(proposal.status || "pending");
  const [loadingAction, setLoadingAction] = useState(null);
  const [result, setResult] = useState(null);

  React.useEffect(() => {
    if ((proposal.status === "cancelled" || proposal.status === "rejected") && status === "pending") {
      setStatus("cancelled");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [proposal.status]);

  const selected = rows.filter((r) => r.checked && !r.exists);

  const toggle = (key) =>
    setRows((prev) => prev.map((r) => (r.key === key ? { ...r, checked: !r.checked } : r)));

  const addRole = (department) => {
    const name = String(drafts[department] || "").trim().slice(0, 50);
    if (!name) return;
    const duplicate = rows.some(
      (r) => r.department_name === department && r.role_name.toLowerCase() === name.toLowerCase()
    );
    if (!duplicate) {
      setRows((prev) => [
        ...prev,
        { key: `${department}:${name.toLowerCase()}:${prev.length}`, role_name: name, department_name: department, exists: false, checked: true },
      ]);
    }
    setDrafts((prev) => ({ ...prev, [department]: "" }));
  };

  const handleAction = async (action) => {
    setLoadingAction(action);
    try {
      const payload = {
        ...(scopeParams || { store_id: homeStore }),
        session_uuid: sessionUuid,
        proposal_id: proposal.proposal_id,
        action,
      };
      if (action === "confirm") {
        payload.overrides = {
          roles: selected.map((r) => ({ role_name: r.role_name, department_name: r.department_name })),
        };
      }
      const res = await postRightworkAPI(confirmUrl, payload);
      if (res.status === 200) {
        const newStatus = action === "confirm" ? "confirmed" : "cancelled";
        const executorResult = res?.data?.redux?.payload?.result;
        setResult(executorResult && typeof executorResult === "object" ? executorResult : null);
        setStatus(newStatus);
        onStatusChange?.(proposal.proposal_id, newStatus);
      } else {
        toast.error("Failed to process action.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.system?.message || err?.response?.data?.error || "Failed to process action.");
    }
    setLoadingAction(null);
  };

  if (status === "confirmed") {
    const created = Array.isArray(result?.created) ? result.created : [];
    return (
      <div className="flex items-start gap-2 px-3 py-2 rounded-lg text-sm my-2 border bg-green-50 border-green-200 text-green-700">
        <CheckCircleIcon fontSize="small" className="mt-0.5 shrink-0 text-green-600" />
        <div className="min-w-0">
          <div className="font-medium">{summary.title || "Roles added"}</div>
          {result?.detail && <div className="text-xs mt-1">{result.detail}</div>}
          {created.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-1.5">
              {created.map((role) => (
                <span key={`${role.department_name}:${role.role_name}`} className="inline-flex items-center gap-1 text-xs">
                  {role.color && (
                    <span aria-hidden="true" className="inline-block w-2.5 h-2.5 rounded-full border border-black/10" style={{ backgroundColor: role.color }} />
                  )}
                  {role.role_name}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  if (status === "cancelled" || status === "rejected") {
    return (
      <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-gray-50 border border-gray-200 text-sm text-gray-500 my-2">
        <CancelIcon fontSize="small" />
        <span>Action cancelled</span>
      </div>
    );
  }

  return (
    <div className="my-2 rounded-lg border border-violet-200 bg-violet-50 overflow-hidden">
      <div className="flex items-center gap-2 px-3 py-2 bg-violet-100 border-b border-violet-200">
        <WorkOutlineIcon fontSize="small" className="text-violet-700" />
        <span className="text-sm font-medium text-violet-800">
          Add {selected.length} {selected.length === 1 ? "role" : "roles"}
        </span>
      </div>
      <div className="px-3 py-3 space-y-3">
        {summary.note && <div className="text-xs text-gray-600">{summary.note}</div>}
        {departments.map((department) => (
          <div key={department}>
            <div className="text-xs font-semibold uppercase tracking-wide text-violet-700 mb-1">{department}</div>
            <div className="flex flex-wrap gap-x-4 gap-y-1">
              {rows
                .filter((r) => r.department_name === department)
                .map((r) => (
                  <label key={r.key} className={`inline-flex items-center gap-1.5 text-sm ${r.exists ? "text-gray-400" : "text-gray-800"}`}>
                    <input
                      type="checkbox"
                      className="accent-violet-700"
                      aria-label={`${r.role_name} in ${department}`}
                      checked={r.checked}
                      disabled={r.exists || loadingAction !== null}
                      onChange={() => toggle(r.key)}
                    />
                    <span>
                      {r.role_name}
                      {r.exists && <span className="text-[11px]"> (already added)</span>}
                    </span>
                  </label>
                ))}
            </div>
            <form
              className="mt-1.5 flex gap-1.5"
              onSubmit={(e) => {
                e.preventDefault();
                addRole(department);
              }}
            >
              <input
                type="text"
                aria-label={`Add a role to ${department}`}
                placeholder="Add a role"
                maxLength={50}
                value={drafts[department] || ""}
                disabled={loadingAction !== null}
                onChange={(e) => setDrafts((prev) => ({ ...prev, [department]: e.target.value }))}
                className="text-sm border border-gray-300 rounded-md px-2 py-1 bg-white w-44 focus:outline-none focus:ring-1 focus:ring-violet-400 focus:border-violet-400"
              />
              <button
                type="submit"
                disabled={loadingAction !== null || !String(drafts[department] || "").trim()}
                className="px-2 py-1 text-xs rounded-md border border-violet-300 text-violet-700 bg-white hover:bg-violet-50 disabled:opacity-50"
              >
                Add
              </button>
            </form>
          </div>
        ))}
      </div>
      <div className="flex justify-end gap-2 px-3 py-2 border-t border-violet-200 bg-white">
        <button
          type="button"
          onClick={() => handleAction("reject")}
          disabled={loadingAction !== null}
          className="px-3 py-1.5 text-sm rounded-md border border-gray-300 text-gray-600 hover:bg-gray-100 disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={() => handleAction("confirm")}
          disabled={loadingAction !== null || selected.length === 0}
          className="px-3 py-1.5 text-sm rounded-md bg-violet-700 text-white hover:bg-violet-800 disabled:opacity-50 flex items-center gap-1"
        >
          {loadingAction === "confirm" && <CircularProgress size={14} sx={{ color: "white" }} />}
          Add roles
        </button>
      </div>
    </div>
  );
}
